import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";

const milestones = [
  {
    label: "Today",
    icon: "🔓",
    filled: true,
    description: "Get instant access to all 8 AI tools and the full video course.",
  },
  {
    label: "Day 4",
    icon: "🔔",
    filled: false,
    description: "We'll remind you about your trial so you stay in control.",
  },
  {
    label: "Day 7",
    icon: "⭐",
    filled: false,
    description: "Your subscription starts. Cancel or upgrade anytime.",
  },
];

export const FreeTrialTimeline = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.unobserve(el);
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    const start = performance.now();
    const duration = 1500;
    const animate = (now: number) => {
      const elapsed = now - start;
      const t = Math.min(elapsed / duration, 1);
      // cubic-bezier(0.16, 1, 0.3, 1) approximation
      const ease = 1 - Math.pow(1 - t, 3);
      setProgress(ease * 100);
      if (t < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [visible]);

  const getMilestoneVisible = (index: number) => {
    if (index === 0) return progress >= 0 && visible;
    if (index === 1) return progress >= 50;
    return progress >= 100;
  };

  const getMilestoneScale = (index: number) => {
    if (index === 0 && progress > 0 && progress < 15) return 1.15;
    if (index === 1 && progress > 45 && progress < 60) return 1.15;
    if (index === 2 && progress > 90) return 1.15;
    return 1;
  };

  return (
    <section ref={sectionRef} className="relative" style={{ background: "#FFFFFF", padding: "80px 0" }}>
      <div className="container mx-auto px-4 md:px-6 text-center">
        <h2
          className="font-display mb-3"
          style={{ color: "#111827", fontSize: "clamp(24px, 3.5vw, 36px)", fontWeight: 800, lineHeight: 1.2 }}
        >
          Your Free Trial, Made Easy
        </h2>
        <p style={{ color: "#6B7280", fontSize: "16px", maxWidth: "560px", margin: "12px auto 0" }}>
          Try AI StoryCraft Studio risk-free for 7 days. Full access to all tools. No commitment, cancel anytime.
        </p>

        {/* Timeline */}
        <div className="relative mx-auto mt-14 mb-14" style={{ maxWidth: "700px" }}>
          {/* Background track */}
          <div
            className="absolute left-0 right-0"
            style={{
              top: "18px",
              height: "3px",
              background: "#E5E7EB",
              borderRadius: "2px",
              marginLeft: "20px",
              marginRight: "20px",
            }}
          />
          {/* Fill track */}
          <div
            className="absolute left-0"
            style={{
              top: "18px",
              height: "3px",
              background: "linear-gradient(90deg, #7C3AED, #A78BFA)",
              borderRadius: "2px",
              width: `${progress}%`,
              marginLeft: "20px",
              maxWidth: "calc(100% - 40px)",
              transition: visible ? "none" : undefined,
            }}
          />

          {/* Milestones */}
          <div className="flex justify-between relative">
            {milestones.map((m, i) => {
              const isVisible = getMilestoneVisible(i);
              const scale = getMilestoneScale(i);
              return (
                <div key={i} className="flex flex-col items-center" style={{ width: "180px" }}>
                  <div
                    className="flex items-center justify-center transition-transform duration-300"
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      background: m.filled ? "#7C3AED" : "white",
                      border: m.filled ? "none" : "2px solid #7C3AED",
                      fontSize: "18px",
                      transform: `scale(${scale})`,
                      zIndex: 1,
                    }}
                  >
                    {m.icon}
                  </div>
                  <div
                    style={{
                      opacity: isVisible ? 1 : 0,
                      transform: isVisible ? "translateY(0)" : "translateY(12px)",
                      transition: "all 0.5s ease",
                    }}
                  >
                    <p style={{ fontWeight: 700, fontSize: "16px", color: "#111827", marginTop: "16px" }}>
                      {m.label}
                    </p>
                    <p style={{ fontWeight: 400, fontSize: "14px", color: "#6B7280", maxWidth: "180px", textAlign: "center", marginTop: "6px", lineHeight: 1.5 }}>
                      {m.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <a href="#pricing">
          <button
            className="group flex items-center gap-2 mx-auto transition-all duration-200 hover:scale-[1.03] active:scale-[0.97]"
            style={{
              background: "#7C3AED",
              color: "white",
              padding: "14px 32px",
              borderRadius: "12px",
              fontWeight: 700,
              fontSize: "16px",
              border: "none",
              cursor: "pointer",
            }}
          >
            Start Your 7-Day Free Trial
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </a>
      </div>
    </section>
  );
};
